import { useEffect, useId, useState } from "react";
import { Search } from "lucide-react";
import {
  FEEDBACK_CATEGORIES,
  FEEDBACK_STATUSES,
  FEEDBACK_SURFACES,
  type FeedbackEvaluationCategory,
  type FeedbackPolarity,
  type FeedbackStatus,
  type FeedbackSurface,
} from "../../types/feedback";

export interface FeedbackFilterState {
  orgId: string;
  surface: FeedbackSurface | "";
  polarity: FeedbackPolarity | "";
  status: FeedbackStatus | "";
  category: FeedbackEvaluationCategory | "";
  q: string;
  fromDate: string;
  toDate: string;
}

export const EMPTY_FILTER: FeedbackFilterState = {
  orgId: "",
  surface: "",
  polarity: "",
  status: "",
  category: "",
  q: "",
  fromDate: "",
  toDate: "",
};

interface Props {
  value: FeedbackFilterState;
  onChange: (next: FeedbackFilterState) => void;
}

/**
 * 300 ms debounce on the free-text `q` field — every other field commits
 * on change. The debounce avoids hammering the lexserver text index on
 * every keystroke while still feeling live.
 */
export function FeedbackFilters({ value, onChange }: Props) {
  const [qDraft, setQDraft] = useState(value.q);
  const idBase = useId();
  const ids = {
    q: `${idBase}-q`,
    orgId: `${idBase}-orgId`,
    fromDate: `${idBase}-fromDate`,
    toDate: `${idBase}-toDate`,
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (qDraft !== value.q) onChange({ ...value, q: qDraft });
    }, 300);
    return () => clearTimeout(t);
    // Only depend on qDraft — debouncing input, not external value updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDraft]);

  // Keep local input in sync if `value.q` is reset externally (e.g. clear-all).
  useEffect(() => {
    setQDraft(value.q);
  }, [value.q]);

  function update<K extends keyof FeedbackFilterState>(key: K, v: FeedbackFilterState[K]) {
    onChange({ ...value, [key]: v });
  }

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex flex-col gap-1">
        <label htmlFor={ids.q} className="text-xs font-medium text-slate-500 dark:text-slate-400">
          Search comments
        </label>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            id={ids.q}
            type="text"
            placeholder="errada, incorreta, vencimento…"
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            className="w-64 rounded-md border border-slate-200 bg-white pl-8 pr-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor={ids.orgId} className="text-xs font-medium text-slate-500 dark:text-slate-400">
          Org ID
        </label>
        <input
          id={ids.orgId}
          type="text"
          placeholder="(any)"
          value={value.orgId}
          onChange={(e) => update("orgId", e.target.value)}
          className="w-48 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800"
        />
      </div>

      <FilterSelect
        label="Polarity"
        value={value.polarity}
        onChange={(v) => update("polarity", v as FeedbackPolarity | "")}
        options={["positive", "negative", "neutral"]}
      />

      <FilterSelect
        label="Surface"
        value={value.surface}
        onChange={(v) => update("surface", v as FeedbackSurface | "")}
        options={FEEDBACK_SURFACES}
      />

      <FilterSelect
        label="Status"
        value={value.status}
        onChange={(v) => update("status", v as FeedbackStatus | "")}
        options={FEEDBACK_STATUSES}
      />

      <FilterSelect
        label="AI Category"
        value={value.category}
        onChange={(v) => update("category", v as FeedbackEvaluationCategory | "")}
        options={FEEDBACK_CATEGORIES}
      />

      <div className="flex flex-col gap-1">
        <label htmlFor={ids.fromDate} className="text-xs font-medium text-slate-500 dark:text-slate-400">
          From
        </label>
        <input
          id={ids.fromDate}
          type="date"
          value={value.fromDate}
          onChange={(e) => update("fromDate", e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor={ids.toDate} className="text-xs font-medium text-slate-500 dark:text-slate-400">
          To
        </label>
        <input
          id={ids.toDate}
          type="date"
          value={value.toDate}
          onChange={(e) => update("toDate", e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800"
        />
      </div>

      <button
        type="button"
        onClick={() => onChange(EMPTY_FILTER)}
        className="ml-auto rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
      >
        Clear
      </button>
    </div>
  );
}

interface SelectProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: readonly string[];
}

function FilterSelect({ label, value, onChange, options }: SelectProps) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-slate-500 dark:text-slate-400">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800"
      >
        <option value="">(any)</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
